import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import MessageDialog from "@/components/admin/MessageDialog";
import NotesPanel from "@/components/admin/NotesPanel";
import { Label } from "@/components/ui/label";
import {
  TIER_LABELS, TIER_ORDER, LEAD_STATUSES, scoreColor, STATUS_COLORS, titleCase, fmtDate,
} from "@/lib/format";
import { toast } from "sonner";
import { Crown, CheckSquare } from "lucide-react";

function Detail({ label, value }) {
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length)) return null;
  return (
    <div className="py-1.5 border-b border-[var(--border-lch)]">
      <div className="text-[11px] uppercase tracking-wide text-[var(--muted)]">{label}</div>
      <div className="text-sm text-[var(--text)]">{Array.isArray(value) ? value.join(", ") : String(value)}</div>
    </div>
  );
}

export default function AdminVipLeads() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [status, setStatus] = useState("new");

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (tier !== "all") params.tier = tier;
    if (status !== "all") params.status = status;
    const r = await api.get("/admin/vip-leads", { params });
    setLeads(r.data);
    setSelected((s) => (s ? r.data.find((x) => x.id === s.id) || null : null));
  }, [q, tier, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const patch = async (url, body, msg) => {
    try { await api.patch(url, body); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addNote = async (note) => { await api.post(`/admin/vip-leads/${selected.id}/note`, { note }); toast.success("Note added"); load(); };
  const createTask = async (l) => {
    try {
      await api.post("/admin/tasks", { title: `Follow up VIP: ${l.name}`, related_type: "vip_lead", related_id: l.id, related_label: l.name, priority: l.lead_score >= 70 ? "high" : "medium" });
      toast.success("Follow-up task created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-vip-leads">
      <div className="flex items-center gap-2 mb-1"><Crown className="text-[#c9a86a]" size={22} /><h1 className="text-2xl font-semibold">VIP Leads</h1></div>
      <p className="text-sm text-[var(--text-2)] mb-4">{leads.length} leads · sorted by newest</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-xs bg-[var(--panel)]" data-testid="lead-search" />
        <Select value={tier} onValueChange={setTier}><SelectTrigger className="sm:w-48 bg-[var(--panel)]"><SelectValue placeholder="Tier" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All tiers</SelectItem>{TIER_ORDER.map((t) => <SelectItem key={t} value={t}>{TIER_LABELS[t]}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="sm:w-44 bg-[var(--panel)]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
      </div>

      <Card className="border-[var(--border-lch)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--panel-2)] text-[var(--text-2)] text-xs uppercase"><tr>
              <th className="text-left p-3">Name</th><th className="text-left p-3">Tier</th>
              <th className="text-left p-3">Score</th><th className="text-left p-3">Timeline</th>
              <th className="text-left p-3">Status</th><th className="text-left p-3">Added</th>
            </tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-[var(--border-lch)] hover:bg-[var(--panel-2)] cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                  <td className="p-3"><div className="font-medium">{l.name}</div><div className="text-xs text-[var(--muted)]">{l.email}</div></td>
                  <td className="p-3"><Badge variant="outline" className="bg-[var(--panel-2)] text-[#c9a86a] border-[#3a352b]">{TIER_LABELS[l.tier] || l.tier}</Badge></td>
                  <td className="p-3"><Badge variant="outline" className={scoreColor(l.lead_score)}>{l.lead_score}</Badge></td>
                  <td className="p-3 text-[var(--text-2)]">{l.buying_timeline || "—"}</td>
                  <td className="p-3"><Badge variant="outline" className={STATUS_COLORS[l.status]}>{titleCase(l.status)}</Badge></td>
                  <td className="p-3 text-[var(--text-2)]">{fmtDate(l.created_at)}</td>
                </tr>
              ))}
              {!leads.length && <tr><td colSpan={6} className="p-8 text-center text-[var(--muted)]">No leads found.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2">{selected.name}
                <Badge variant="outline" className={scoreColor(selected.lead_score)}>Score {selected.lead_score}</Badge></DialogTitle></DialogHeader>
              <div className="flex flex-wrap gap-2 mb-3">
                <MessageDialog channel="email" to={selected.email} relatedType="vip_lead" relatedId={selected.id} label="Email Lead" />
                <MessageDialog channel="sms" to={selected.phone} relatedType="vip_lead" relatedId={selected.id} label="Text Lead" />
                <Button size="sm" variant="outline" onClick={() => createTask(selected)} data-testid="create-task-btn"><CheckSquare size={14} className="mr-1" />Create Follow-up Task</Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6">
                <div>
                  <Detail label="Email" value={selected.email} />
                  <Detail label="Phone" value={selected.phone} />
                  <Detail label="Preferred Contact" value={titleCase(selected.preferred_contact)} />
                  <Detail label="Email Consent" value={selected.email_consent ? "Yes" : "No"} />
                  <Detail label="SMS Consent" value={selected.sms_consent ? "Yes" : "No"} />
                  <Detail label="Merch Interest" value={selected.merch_interest ? "Yes" : "No"} />
                </div>
                <div>
                  <Detail label="Budget" value={selected.budget_range} />
                  <Detail label="Timeline" value={selected.buying_timeline} />
                  <Detail label="Interest Type" value={selected.interest_type} />
                  <Detail label="Favorite Brands" value={selected.favorite_brands} />
                  <Detail label="Preferences" value={selected.watch_preferences} />
                  <Detail label="Notes" value={selected.notes} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div className="space-y-1.5"><Label className="text-xs text-[var(--text-2)]">Tier</Label>
                  <Select value={selected.tier} onValueChange={(v) => patch(`/admin/vip-leads/${selected.id}/tier`, { tier: v }, "Tier updated")}>
                    <SelectTrigger className="bg-[var(--panel)]" data-testid="lead-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIER_ORDER.map((t) => <SelectItem key={t} value={t}>{TIER_LABELS[t]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs text-[var(--text-2)]">Status</Label>
                  <Select value={selected.status} onValueChange={(v) => patch(`/admin/vip-leads/${selected.id}/status`, { status: v }, "Status updated")}>
                    <SelectTrigger className="bg-[var(--panel)]" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4"><Label className="text-xs text-[var(--text-2)] mb-2 block">Internal Notes</Label>
                <NotesPanel notes={selected.notes_log} onAdd={addNote} /></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
